import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import { MenuItem } from '@mui/material';

import ActionMenu from 'components/ActionMenu';
import CTA from 'components/CallToActions';
import CostCenterForm, {
  CostCenterFormValues
} from 'components/Forms/CostCenter';
import { LoadingAnimation } from 'components/LoadingAnimation';
import Page, { ActionType } from 'components/Page';
import PageHeader from 'components/PageHeader/PageHeader';
import SearchInput from 'components/SearchInput';
import AddCard from 'components/svg/AddCard';
import FoodoraWallet from 'components/svg/FoodoraWallet';
import TableList, { RowsPerPage, TableFilterRow } from 'components/TableList';
import { Column } from 'components/TableList/SortableHeader';
import { FormikHelpers, FormikProps } from 'formik';
import useAction from 'hooks/useAction';
import useApi from 'hooks/useApi';
import useNotification from 'hooks/useNotification';
import useQueryParam from 'hooks/useQueryParam/useQueryParam';
import useQueryWrapperP0 from 'hooks/useQueryWrapperP0';
import useQueryWrapperP1 from 'hooks/useQueryWrapperP1';
import CostCenterQueryKeys from 'query-keys/cost-center';
import { CostCenter, CostCenterPayload } from 'services/api';
import { dispatchGaEvent, PageType } from 'services/google-analytics';
import { AppRoute } from 'share/types/routes';
import isFoodora from 'src/utils/is-foodora/is-foodora';
import generateQuery from 'utils/generate-query';

import {
  Actions,
  add_cost_center_button,
  add_title,
  ButtonText,
  cost_center_empty_cta_subtitle,
  cost_center_empty_cta_title,
  deleteDialogContent,
  edit_title,
  initialValues,
  search_place_holder_text,
  title
} from './constants';
import { DialogBtnProps, TestID } from './types';

const DeleteDialogBtn = ({ onClick }: DialogBtnProps) => (
  <>
    <Button
      variant="secondary"
      onClick={() => {
        return;
      }}
    >
      Back to Cost centers
    </Button>
    <Button onClick={onClick} marginLeft="sm">
      Delete
    </Button>
  </>
);

export function CostCenterContainer() {
  const api = useApi();
  const queryParam = useQueryParam();
  const [, setAction, { id: id_str }] = useAction<Actions>();
  const id = parseInt(id_str);
  const queryClient = useQueryClient();
  const { pushNotification, dismissNotification } = useNotification();
  const [keepSidebarOpen, setSidebarOpen] = useState(false);
  const [costCenterName, setCostCenterName] = useState('');
  const columns: Column<CostCenter>[] = [
    {
      id: 'name',
      options: { disablePadding: false },
      label: 'Cost center name'
    },
    {
      id: 'total_users',
      options: { disablePadding: false },
      label: 'No. of employees'
    }
  ];

  const navigate = useNavigate();

  useEffect(() => {
    dispatchGaEvent({ pageType: PageType.COST_CENTER });
  }, []);

  const filters = ['search', 'count', 'offset'];
  const filtered = Object.keys(queryParam)
    .filter((key) => filters.includes(key))
    .reduce((param: Record<string, string>, key) => {
      param[key] = queryParam[key];
      return param;
    }, {});

  // queries
  const costCenters = useQueryWrapperP0(
    CostCenterQueryKeys.list(filtered),
    async () => {
      return api.company.costCenter.list(filtered);
    }
  );

  const costCenter = useQueryWrapperP1(
    CostCenterQueryKeys.detail(id),
    async () => {
      const response = await api.company.costCenter.get(id);
      return {
        name: response.data.name
      };
    },
    {
      enabled: Boolean(id)
    }
  );

  // mutations
  const edit = useMutation(
    (costCenter: { id: number; payload: CostCenterPayload }) => {
      return api.company.costCenter.update(costCenter.id, costCenter.payload);
    }
  );

  const deleteLocation = useMutation(async (id: number) => {
    return api.company.costCenter.delete(id);
  });

  const create = useMutation(async (payload: CostCenterPayload) => {
    return api.company.costCenter.create(payload);
  });

  // handlers
  const handleClick = (type: Actions, id?: string) => {
    if (id) setAction(type, true, { id });
    else setAction(type);
  };

  const onEdit = (id: number) => {
    return () => {
      handleClick(Actions.Edit, id.toString());
    };
  };

  const handleDeleteRow = (row: CostCenter) => {
    return () => {
      setCostCenterName(row.name);
      handleClick(Actions.Delete, row.id.toString());
    };
  };

  const handleCreate = (
    values: CostCenterFormValues,
    actions: FormikHelpers<CostCenterFormValues>
  ) => {
    const payload: CostCenterPayload = {
      name: values.name
    };

    const loading = pushNotification(`Adding ${payload.name}...`);
    create.mutate(payload, {
      onSuccess: () => {
        dismissNotification(loading);

        queryClient.invalidateQueries(CostCenterQueryKeys.list()).then(() => {
          pushNotification(`${payload.name} added!`, {
            variant: 'success'
          });
          setAction(Actions.Add, keepSidebarOpen);
          actions.resetForm();
        });
      },
      onError: (e) => {
        dismissNotification(loading);

        const error = e as Error;
        const error_messge_id = pushNotification(`${error.message}`, {
          variant: 'error',
          onClick: () => {
            actions.submitForm();
            dismissNotification(error_messge_id);
          },
          autoHideDuration: 10000
        });
      },
      onSettled: () => {
        actions.setSubmitting(false);
      }
    });
  };

  const handleEdit = (
    values: CostCenterFormValues,
    actions: FormikHelpers<CostCenterFormValues>
  ) => {
    const payload: CostCenterPayload = {
      name: values.name
    };

    const loading = pushNotification(`Updating ${payload.name}...`);

    edit.mutate(
      { id, payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(CostCenterQueryKeys.list());
          queryClient
            .refetchQueries(CostCenterQueryKeys.detail(id))
            .then(() => {
              pushNotification(`${payload.name} updated`, {
                variant: 'success'
              });
              setAction(Actions.Edit, false);
              actions.resetForm();
            });
        },
        onError: (e) => {
          const error = e as Error;
          const error_messge_id = pushNotification(`${error.message}`, {
            variant: 'error',
            onClick: () => {
              actions.submitForm();
              dismissNotification(error_messge_id);
            },
            autoHideDuration: 10000
          });
        },
        onSettled: () => {
          dismissNotification(loading);
          actions.setSubmitting(false);
        }
      }
    );
  };

  const onPageChange = (
    changes: Record<string, string | number | undefined>
  ) => {
    let params = {
      ...queryParam,
      ...changes
    };

    params = Object.keys(params).reduce((acc, key) => {
      params[key] !== undefined && (acc[key] = params[key]);
      return acc;
    }, {} as Record<string, any>);

    navigate(
      {
        pathname: AppRoute.CostCenter,
        search: generateQuery(params)
      },
      { replace: true }
    );
  };

  const handleSearch = (value: string) => {
    onPageChange({ search: value || undefined, offset: undefined });
    return;
  };

  const handleDelete = () => {
    deleteLocation.mutate(id, {
      onSuccess: () => {
        queryClient
          .invalidateQueries(CostCenterQueryKeys.list(filtered))
          .then(() => {
            pushNotification(`${costCenterName} deleted!`, {
              variant: 'success'
            });
          });
      },
      onError: (e) => {
        const error = e as Error;
        const error_message_id = pushNotification(`${error.message}`, {
          variant: 'error',
          onClick: () => {
            dismissNotification(error_message_id);
          },
          autoHideDuration: 10000
        });
      }
    });
  };

  const handlePaginationChange = (page: number, rowsPerPage: number) => {
    onPageChange({
      ...{
        offset: page ? page * rowsPerPage : undefined,
        count: rowsPerPage
      }
    });
  };

  const page = queryParam.offset ? parseInt(queryParam.offset) : undefined;
  const rowsPerPage = queryParam.count ? parseInt(queryParam.count) : undefined;
  const tableListProps = {
    ...(page && {
      page: Math.floor(page / (rowsPerPage || RowsPerPage.TWENTYFIVE))
    }),
    ...(rowsPerPage && { rowsPerPage })
  };

  const detailFormRef = useRef<FormikProps<CostCenterFormValues>>(null);

  return (
    <Page
      actions={{
        [Actions.Add]: {
          component: (
            <CostCenterForm
              onSubmit={handleCreate}
              initialValues={initialValues}
              detailFormRef={detailFormRef}
              buttons={() => (
                <>
                  <Button
                    key="1"
                    size="small"
                    style={{ width: '160px' }}
                    variant="secondary"
                    type="submit"
                    disabled={detailFormRef.current?.isSubmitting}
                    onClick={() => {
                      setSidebarOpen(true);
                      detailFormRef.current?.handleSubmit();
                    }}
                  >
                    {ButtonText.SaveAndNext}
                  </Button>
                  <Button
                    key="2"
                    size="small"
                    style={{ width: '160px' }}
                    type="submit"
                    disabled={detailFormRef.current?.isSubmitting}
                    onClick={() => {
                      setSidebarOpen(false);
                      detailFormRef.current?.handleSubmit();
                    }}
                  >
                    {ButtonText.Save}
                  </Button>
                </>
              )}
            />
          ),
          type: ActionType.RightDrawer,
          options: {
            title: add_title
          }
        },
        [Actions.Edit]: {
          component: !costCenter.isLoading ? (
            <CostCenterForm
              onSubmit={handleEdit}
              detailFormRef={detailFormRef}
              initialValues={costCenter.data || initialValues}
              buttons={() => (
                <div style={{ width: '100%' }}>
                  <Button
                    key="1"
                    size="small"
                    layout="full-width-primary"
                    type="submit"
                    onClick={() => {
                      detailFormRef.current?.handleSubmit();
                    }}
                    disabled={detailFormRef.current?.isSubmitting}
                  >
                    {ButtonText.Save}
                  </Button>
                </div>
              )}
            />
          ) : (
            <LoadingAnimation />
          ),
          type: ActionType.RightDrawer,
          options: {
            title: edit_title
          }
        },
        [Actions.Delete]: {
          component: <DeleteDialogBtn onClick={handleDelete} />,
          type: ActionType.AppDialog,
          options: {
            title: `Delete ${costCenterName} cost center?`,
            content: deleteDialogContent
          }
        }
      }}
    >
      <PageHeader
        title={title}
        buttons={[
          <Button
            onClick={() => handleClick(Actions.Add)}
            data-testid={TestID.AddButton}
            key="add-cost-center"
          >
            {add_cost_center_button}
          </Button>
        ]}
      />
      <TableList
        rowsPerPage={rowsPerPage}
        total={
          costCenters.error ? 0 : costCenters.data ? costCenters.data.total : -1
        }
        data={costCenters.data?.data || []}
        columns={columns}
        loading={costCenters.isLoading}
        {...tableListProps}
        onPaginationChange={handlePaginationChange}
        empty={
          Object.keys(filtered).length == 0 && (
            <CTA
              illustration={isFoodora() ? FoodoraWallet : AddCard}
              title={cost_center_empty_cta_title}
              subtitle={cost_center_empty_cta_subtitle}
            >
              <Button
                key="1"
                onClick={() => handleClick(Actions.Add)}
                data-testid={TestID.AddButtonCTA}
              >
                {add_cost_center_button}
              </Button>
            </CTA>
          )
        }
        action={(row: CostCenter) => (
          <ActionMenu>
            <MenuItem onClick={onEdit(row.id)} style={{ minWidth: 200 }}>
              Edit
            </MenuItem>
            <MenuItem onClick={handleDeleteRow(row)} style={{ minWidth: 200 }}>
              Delete
            </MenuItem>
          </ActionMenu>
        )}
      >
        <TableFilterRow>
          <SearchInput
            value={queryParam.search || ''}
            onSearch={handleSearch}
            placeholder={search_place_holder_text}
            sx={{ width: 480 }}
          />
        </TableFilterRow>
      </TableList>
    </Page>
  );
}
